import React, { useEffect, useState } from "react";
import { Avatar, Button, Flex, List, Modal } from "antd";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { storageService } from "../../../services/storageService";
import { RootState } from "../../../redux/store";
import { useSocket } from "../../../context/SocketContext";
import { UserAvatarIcon } from "../../../components/icons";
import MiniCallScreen from "../../../components/calls/MiniCallScreen";
import CallControls from "../../../components/calls/CallControls";
import CallMemberDetails from "../../../components/calls/CallMemberDetails";
import ContactCard from "../../../components/Chat/ContactCard";
import SearchInput from "../../../components/common/UI/SearchInput";
import "./index.scss";

interface GroupCallScreenProps {}

interface CallMember {
  groupName: string;
  group: string;
  accountId: string;
  registeredNumber: string;
  profileImage: string;
  fullName: string;
  joined: boolean;
  invited: boolean;
  startTalking: boolean;
  isMute: boolean;
  action: string;
  loading?: boolean;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const GroupCallScreen: React.FC<GroupCallScreenProps> = () => {
  const userId = storageService.getUserId();
  const { socket } = useSocket();
  const { calls, currentCallId } = useSelector((state: RootState) => state.sip);
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const onGoingCalls = Object.keys(calls);
  const [showCallScreen, setShowCallScreen] = useState(true);
  const [membersIncall, setMembersInCall] = useState<CallMember[]>([]);
  const [userInCall, setUserInCall] = useState<boolean>(true);
  const [elapsed, setElapsed] = useState(0);
  const [groupName, setGroupName] = useState("");
  const [addParticipantModalOpen, setAddParticipantModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalCurrentScreen, setModalCurrentScreen] = useState("invited");

  const filteredMembers = membersIncall.filter((member: CallMember) => {
    if (!searchTerm.trim()) return true;
    return (
      member.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.registeredNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleAddParticipant = (member: CallMember) => {
    if (!socket || member?.loading) return;

    socket.emit("invite-member", {
      groupId: member.group,
      userId: member.accountId,
    });

    setMembersInCall((prev) =>
      prev.map((m) =>
        m.accountId === member.accountId ? { ...m, loading: true } : m
      )
    );
  };

  useEffect(() => {
    if (!socket) return;

    socket?.on("groupCall", (message: any) => {
      const user = message?.data?.find(
        (member: CallMember) => member?.accountId === userId
      );

      setUserInCall(() => {
        if (user?.invited && user?.joined) {
          return true;
        }

        return false;
      });
      setMembersInCall(message.data);
      setGroupName(message.data[0].groupName);
    });

    return () => {
      socket.off("groupCall", () => {
        setMembersInCall([]);
        setGroupName("");
      });
    };
  }, [socket]);

  useEffect(() => {
    if (!calls || !currentCallId) return;
    const currentCall = calls[currentCallId];
    if (!currentCall?.startTime) return;

    const tick = () => {
      const now = Date.now();
      setElapsed(Math.floor((now - currentCall.startTime!) / 1000));
    };

    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [calls, currentCallId]);

  useEffect(() => {
    if (groupName) return;
    if (currentChat?.name) {
      setGroupName(currentChat.name);
    }
  }, [currentChat]);

  if (
    // !membersIncall.length ||
    !currentCallId ||
    !calls[currentCallId] ||
    (calls[currentCallId].status !== "in-progress" &&
      calls[currentCallId].status !== "calling") ||
    !userInCall ||
    !calls[currentCallId]?.isGroupCall
  ) {
    return <></>;
  }

  return (
    <>
      {showCallScreen ? (
        <div
          className={`group-call-screen-wrapper ${
            !onGoingCalls.length || !currentCallId ? "display-none" : ""
          }`}
        >
          {onGoingCalls.length > 0 && currentCallId ? (
            <div className="calls-screen">
              <Flex justify="space-between">
                <ArrowLeftOutlined
                  className="back-icon"
                  onClick={() => setShowCallScreen(false)}
                />
                <div className="group-details">
                  <Flex vertical gap={4}>
                    <div className="grp-name">{groupName}</div>
                    <div className="timestamp">
                      {calls[currentCallId]?.startTime
                        ? formatTime(elapsed)
                        : ""}
                    </div>
                  </Flex>
                </div>
                <Button
                  shape="circle"
                  className="add-participant-btn"
                  icon={<UserAvatarIcon />}
                  onClick={() => setAddParticipantModalOpen(true)}
                ></Button>
              </Flex>
              <div
                className="calls-grid"
                style={{
                  gridTemplateColumns: `repeat(${
                    membersIncall.length > 2 ? "2" : membersIncall.length
                  }, 1fr)`,
                }}
              >
                {membersIncall?.map((member: CallMember) => {
                  if (member.invited && member.joined) {
                    return (
                      <CallMemberDetails
                        key={member.accountId}
                        member={member}
                      />
                    );
                  }
                  return null;
                })}
              </div>
              <CallControls
                currentCall={calls[currentCallId]}
                showDTMF={false}
              />
              <Modal
                open={addParticipantModalOpen}
                onCancel={() => setAddParticipantModalOpen(false)}
                className="add-participant-modal"
                footer={null}
                closeIcon={null}
              >
                {modalCurrentScreen === "new-member" && (
                  <Flex>
                    <LeftOutlined
                      className="back-icon"
                      onClick={() => setModalCurrentScreen("invited")}
                    />
                    <SearchInput value={searchTerm} onChange={setSearchTerm} />
                  </Flex>
                )}

                {modalCurrentScreen === "invited" && (
                  <Button
                    type="text"
                    className="add-new-btn"
                    onClick={() => {
                      setModalCurrentScreen("new-member");
                    }}
                  >
                    <Avatar className="add-participant-avatar">
                      <UserAddOutlined />
                    </Avatar>
                    Add new participant
                  </Button>
                )}

                <List
                  className="members-list"
                  header={
                    modalCurrentScreen === "invited"
                      ? "Invited members"
                      : "Add new members"
                  }
                  dataSource={
                    modalCurrentScreen === "invited"
                      ? membersIncall?.filter(
                          (item: CallMember) => item.invited && !item.joined
                        )
                      : filteredMembers?.filter(
                          (item: CallMember) => !item.invited && !item.joined
                        )
                  }
                  renderItem={(item: CallMember) => (
                    <ContactCard
                      onClick={() => handleAddParticipant(item)}
                      key={item?.accountId}
                      phoneNumber={item?.registeredNumber}
                      userName={item?.fullName}
                      loading={item?.loading}
                    />
                  )}
                />
              </Modal>
            </div>
          ) : null}
        </div>
      ) : (
        <MiniCallScreen
          groupName={groupName}
          callTimer={formatTime(elapsed)}
          onClick={() => setShowCallScreen(!showCallScreen)}
          numberOfCalls={onGoingCalls.length}
          currentCall={calls[currentCallId]}
        />
      )}
    </>
  );
};

export default GroupCallScreen;
